/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterOutlet } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { CalculationKpis } from "../../shared/models/http/calculator/calculation-kpis.model";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { DashboardHardwareAssetsService } from "../../shared/services/dashboard-hardware-assets.service";
import { StorageService } from "../../shared/services/storage.service";
import { PATHS, RESOURCE_TYPES } from "../../shared/utils/constants";
import { CalculatorService } from "./../../shared/services/calculator.service";
import { MaxDecimalsPipe } from "../../shared/pipes/max-decimals.pipe";

@Component({
	selector: "globalictcc-calculator",
	standalone: true,
	imports: [
		ButtonComponent,
		DropdownComponent,
		ReactiveFormsModule,
		RouterOutlet,
		AsyncPipe,
		MaxDecimalsPipe
	],
	templateUrl: "./calculator.component.html",
	styleUrl: "./calculator.component.scss"
})
export class CalculatorComponent implements OnInit, OnDestroy {
	public paths = PATHS;
	public resourceForm = new FormGroup({
		resourceType: new FormControl("", [Validators.required])
	});

	public resourceTypes: SelectOption[] = RESOURCE_TYPES;
	public calculation$: Observable<CalculationKpis | null>;

	private _tenantId: string = "";
	private _destroy$ = new Subject<void>();

	constructor(
		private _router: Router,
		private _calculatorService: CalculatorService,
		private _storageService: StorageService,
		private _dashboardHardwareAssetsService: DashboardHardwareAssetsService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		const resourceType = this._router.url.split("/").pop();
		if (resourceType) this.resourceForm.controls.resourceType.setValue(resourceType);

		this.calculation$ = this._calculatorService.calculation$;
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Navigates back to the previous page using the environment service.
	 * This method is typically called when the user wants to go back to the previous view.
	 */
	public onGoBack() {
		this._router.navigate([PATHS.TOOLBOX]);
	}

	/**
	 * Handles the resource application action.
	 *
	 * Retrieves the selected resource type from the form controls. If a resource type is selected,
	 * navigates to the corresponding route using the Angular Router. If no resource type is selected,
	 * the method exits without performing any action.
	 *
	 * @remarks
	 * This method is typically triggered by a user action, such as clicking an "Apply" button in the UI.
	 */
	public onResourceApply() {
		const resourceType = this.resourceForm.controls.resourceType.value;

		if (!resourceType) return;

		this._calculatorService.setCalculation(null);
		this._router.navigate([`${PATHS.CALCULATOR}/${resourceType}`]);
	}

	/**
	 * Saves the current calculation for the specified tenant.
	 *
	 * This method retrieves the current calculation ID from the calculator service.
	 * If either the calculation ID or tenant ID is missing, the method returns early.
	 * Otherwise, it calls the dashboard hardware assets service to save the calculation
	 * for the given tenant and subscribes to the resulting observable.
	 *
	 * @remarks
	 * This method does not handle the observable's result or errors explicitly.
	 */
	public onSaveCalculation() {
		const calculationId = this._calculatorService.calculationId;

		if (!calculationId || !this._tenantId) return;

		this._dashboardHardwareAssetsService
			.saveCalculation(calculationId, this._tenantId)
			.subscribe();
	}
}
